import "./styles.less";

import { Col, Form, Row, Tabs, Typography } from "antd";
import React, { ReactElement, memo, useState, useContext } from "react";

import { PropComponentMode } from "../PropertyInterfaces";
import { ScheduleVisitForm } from "./ScheduleVisitForm/web";
import { TextForm } from "./TextForm/web";
import { useTheme } from "../../../Styles/ThemeHook";
import { useRouter } from "next/router";
import { ConfigStateContext } from "../../../Contexts/Configurations/context";
import { OwnerInfo } from "../OwnerInfo/web";
import { BoxArrowIcon } from "shared-components/Components/CustomIcons/BoxArrowIcon/web";
import { getInmoLink } from "src/utils/Functions";
import { Owner } from "shared-components";

const { Paragraph } = Typography;

interface InformationRequestProps {
	id: number | string;
	children?: ReactElement;
	onComplete?: () => void;
	showDisclaimer?: boolean;
	identifier?: number;
	mode?: PropComponentMode;
	disabled?: boolean;
	defaultMessage?: string;
	isInListing?: boolean;
	showCTA?: boolean;
	queryRouter?: string;
	onlyConsult?: boolean;
	isButtonTab?: boolean;
	activeButtonTab?: string;
	onChangeButtonTab?: (tab: any) => void;
	isProjectSpecialPage?: boolean;
	slug?: string;
	projectId?: number;
	owner?: Owner;
	inmoPropsLink?: string;
	isProject?: boolean;
	isFromModal?: boolean;
	propertyLink?: string;
}

export const InformationRequest = memo(
	({
		id,
		children,
		onComplete = null,
		showDisclaimer = true,
		identifier = 1,
		mode = "auto",
		disabled = false,
		defaultMessage = "",
		isInListing = false,
		showCTA = false,
		isButtonTab = false,
		onlyConsult = false,
		activeButtonTab,
		onChangeButtonTab,
		queryRouter = "",
		isProjectSpecialPage = false,
		slug = "",
		projectId = null,
		owner = null,
		inmoPropsLink = undefined,
		isProject = false,
		isFromModal = false,
		propertyLink = undefined
	}: InformationRequestProps) => {
		const { TabPane } = Tabs;
		const { theme } = useTheme();
		const { query } = useRouter();
		const { country_code } = useContext(ConfigStateContext);

		const form_span = children ? { span: 24, lg: 12 } : { span: 24 };

		const isFromPaidTraffic = (function() {
			if (country_code === "BR") {
				if (query.gclid != null) {
					return "GO";
				} else if (query.trovit != null) {
					return "trovit";
				} else if (query["lifull-connect"] != null) {
					return "lifull-connect";
				}
			}

			return null;
		})();

		const children_holder = children ? (
			<Col span={24} lg={12}>
				{children}
				{country_code !== "CO" && owner?.type !== null && (
					<Typography.Link
						className="see-more-props"
						href={inmoPropsLink}
						target="_blank">
						<span>Ver todas las propiedades</span>
						<BoxArrowIcon width={18} />
					</Typography.Link>
				)}

				{country_code === "CO" && owner?.type !== null && (
					<Typography.Link
						className="fincaRaiz-see-more-props"
						href={getInmoLink(owner?.id, owner?.name)}
						target="_blank">
						<span>
							Ver Perfil{" "}
							{owner?.type == "desarrollador" ? "de Constructora" : "de Inmobiliaria"}{" "}
						</span>
						<BoxArrowIcon width={18} />
					</Typography.Link>
				)}
			</Col>
		) : null;

		const disclaimer = false ? (
			<Col
				span={24}
				lg={{ push: children ? 10 : 0, span: children ? 14 : 24 }}
				className={"information-request-disclaimer"}>
				<Paragraph type={"secondary"}>
					Al enviar una consulta, usted acepta que InfoCasas y los profesionales de bienes
					raíces pueden contactarlo por teléfono / mensaje de texto sobre su consulta, lo
					que puede implicar el uso de medios automatizados. También acepta nuestros de
					InfoCasas no respalda a ningún profesional de bienes raíces
				</Paragraph>
			</Col>
		) : null;

		const top_form =
			showCTA && country_code !== "BR" ? (
				<Form.Item>
					<OwnerInfo
						owner={owner}
						id={id}
						mode={mode}
						disabled={disabled}
						isProject={isProject}
					/>
				</Form.Item>
			) : null;

		const [leadForm] = Form.useForm();
		const [schedulevisitForm] = Form.useForm();

		const TABKEY_LEADFORM = "TABKEY_LEADFORM";
		const TABKEY_SCHEDULEVISITFORM = "TABKEY_SCHEDULEVISITFORM";
		const [activeTab, setActiveTab] = useState(TABKEY_LEADFORM);

		return (
			<Tabs
				className={`property-information-request ${activeTab} ${country_code} ${
					country_code === "CO" ? "tabTitle" : ""
				}`}
				activeKey={isButtonTab ? activeButtonTab : activeTab}
				type="card"
				size="small"
				tabBarGutter={0}
				tabBarStyle={{
					display: "none",
				}}
				onChange={key => {
					if (key === (isButtonTab ? activeButtonTab : activeTab)) {
						return;
					}
					isButtonTab ? onChangeButtonTab(key) : setActiveTab(key);
				}}>
				<TabPane
					tab={country_code === "ce3" ? "Consultá Ahora" : "Consultar"}
					key={TABKEY_LEADFORM}>
					<Row gutter={[theme.spacing.xxlSpacing, 0]}>
						{children_holder}
						<Col {...form_span}>
							{top_form}
							<TextForm
								id={id}
								onComplete={onComplete}
								defaultMessage={defaultMessage}
								mode={mode}
								disabled={disabled}
								isInListing={isInListing}
								showCTA={showCTA}
								onlyConsult={onlyConsult}
								queryRouter={queryRouter}
								isProjectSpecialPage={isProjectSpecialPage}
								slug={slug}
								projectId={projectId}
								isFromPaidTraffic={isFromPaidTraffic}
								isProject={isProject}
								owner={owner}
								isFromModal={isFromModal}
								propertyLink={propertyLink}
							/>
						</Col>
						{disclaimer}
					</Row>
				</TabPane>
				{!onlyConsult && false && country_code !== "ce3" && (
					<TabPane tab="Agendar Visita" key={TABKEY_SCHEDULEVISITFORM}>
						<Row gutter={[theme.spacing.xxlSpacing, 0]}>
							{children_holder}
							<Col {...form_span}>
								{top_form}
								<ScheduleVisitForm
									id={id}
									form={schedulevisitForm}
									onComplete={onComplete}
									identifier={identifier}
									mode={mode}
									disabled={disabled}
									isInListing={isInListing}
									isProjectSpecialPage={isProjectSpecialPage}
									slug={slug}
									projectId={projectId}
									isFromPaidTraffic={isFromPaidTraffic}
									isProject={isProject}
								/>
							</Col>
							{disclaimer}
						</Row>
					</TabPane>
				)}
			</Tabs>
		);
	}
);

